import {
  Alert,
  Box,
  Grid,
  Skeleton,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { MemreHeader, MemrePageContainer } from 'components/core';
import UnenrollConfirmationDialog from 'components/modals/UnenrollConfirmationDialog';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useUnenroll } from 'hooks/useUnenroll';
import React, { useState } from 'react';
import {
  AnimatedGridItem,
  CourseCard,
  CourseSkeletons,
  EmptyState,
  LibraryIntroBanner,
  LibraryPagination,
  LibrarySearchControls,
} from './components';
import { useLibraryCourses, usePartnerLibrary } from './hooks';
import { TCourse } from 'types';

// Main partner library component
export const PartnerLibrary: React.FC = () => {
  const user = useCurrentUser();
  const partner = user?.primaryPartner;
  const partnerId = partner?.id;
  const userId = user?.id;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [sortOrder, setSortOrder] = useState<'name' | '-name'>('name');
  const [processingCourseId, setProcessingCourseId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [courseToUnenroll, setCourseToUnenroll] = useState<TCourse | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [showIntro, setShowIntro] = useState(true);

  // Fetch data
  const { data: partnerLibrary, isLoading: isLibraryLoading, error: libraryError } = usePartnerLibrary(partnerId);
  const {
    data: coursesData,
    isLoading: isCoursesLoading,
    error: coursesError,
    refetch: refetchCourses,
  } = useLibraryCourses(partnerId, currentPage, pageSize, searchQuery, sortOrder);

  // Initialize the unenroll hook
  const {
    isLoading: isUnenrolling,
    error: unenrollError,
    errorMessage,
    isConfirmationOpen,
    openConfirmation,
    closeConfirmation,
    executeUnenroll,
  } = useUnenroll({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Successfully unenrolled from course.',
        severity: 'info',
      });
      refetchCourses();
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  });

  // Toggle sort order (A-Z or Z-A)
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'name' ? '-name' : 'name');
    setCurrentPage(1);
  };

  // Handle course enrollment
  const handleEnroll = async (courseId: string) => {
    if (!userId) {
      setSnackbar({
        open: true,
        message: 'You must be logged in to enroll in a course.',
        severity: 'error',
      });
      return;
    }

    setProcessingCourseId(courseId);
    setIsProcessing(true);

    try {
      // Check if the course is already enrolled
      const course = coursesData?.courses.find((c) => c.id === courseId);
      if (course?.meta?.isEnrolled) {
        handleViewCourse(courseId);
        return;
      }

      // Make the API call to enroll
      await axios.post(`/api/v3/courses/${courseId}/users`, {
        course_id: Number(courseId),
        user_id: Number(userId),
      });

      setSnackbar({
        open: true,
        message: 'Successfully enrolled in course!',
        severity: 'success',
      });

      // Refetch courses to update enrollment status
      refetchCourses();
    } catch (error) {
      console.error('Error enrolling in course:', error);
      setSnackbar({
        open: true,
        message: 'Failed to enroll in course. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsProcessing(false);
      setProcessingCourseId(null);
    }
  };

  // Handle course unenrollment
  const handleUnenroll = (courseId: string) => {
    if (!userId) return;
    
    // Find the course to display its name in the confirmation dialog
    const course = coursesData?.courses.find(c => c.id === courseId);
    setCourseToUnenroll(course || null);
    
    // Open the confirmation dialog
    openConfirmation(courseId, userId);
  };

  // Handle viewing a course
  const handleViewCourse = (courseId: string) => {
    window.location.href = `/app/nav/v4/dashboard/courses/${courseId}`;
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Display errors if they occur
  if (libraryError || coursesError) {
    return (
      <MemrePageContainer>
        <Alert severity="error" sx={{ mb: 4 }}>
          {libraryError ? 'Failed to load partner library information.' : 'Failed to load courses.'}
          Please try refreshing the page.
        </Alert>
      </MemrePageContainer>
    );
  }

  return (
    <>
      {/* Unenroll Confirmation Dialog */}
      <UnenrollConfirmationDialog
        open={isConfirmationOpen}
        isLoading={isUnenrolling}
        courseName={courseToUnenroll?.name || 'this course'}
        onConfirm={executeUnenroll}
        onCancel={closeConfirmation}
      />
      
      <MemreHeader text={isLibraryLoading ? 'Partner Library' : partnerLibrary?.name || 'Partner Library'} />
      <MemrePageContainer>
        {/* Introduction Banner */}
        <LibraryIntroBanner
          libraryName={partnerLibrary?.name}
          onClose={() => setShowIntro(false)}
          show={showIntro}
        />

        {/* Header and Search Controls */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight={700}
              color="primary.main"
              sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}
            >
              {isLibraryLoading ? <Skeleton width={300} /> : 'Explore Courses'}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {isLibraryLoading || isCoursesLoading ? (
                <Skeleton width={200} />
              ) : (
                `${coursesData?.totalCount || 0} course${
                  coursesData?.totalCount !== 1 ? 's' : ''
                } available for enrollment`
              )}
            </Typography>
          </Box>

          {/* Search and Sort Controls */}
          <LibrarySearchControls
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortOrder={sortOrder}
            toggleSortOrder={toggleSortOrder}
          />
        </Box>

        {/* Course Grid */}
        {isCoursesLoading ? (
          <CourseSkeletons count={3} />
        ) : (
          <>
            <Grid container spacing={3}>
              {coursesData?.courses.map((course, index) => (
                <AnimatedGridItem item xs={12} sm={6} md={4} key={course.id} animationIndex={index}>
                  <CourseCard
                    course={course}
                    onEnroll={handleEnroll}
                    onUnenroll={handleUnenroll}
                    onViewCourse={handleViewCourse}
                    isProcessing={isProcessing || (isUnenrolling && courseToUnenroll?.id === course.id)}
                    processingCourseId={processingCourseId}
                  />
                </AnimatedGridItem>
              ))}
            </Grid>

            {/* Empty State */}
            {!coursesData?.courses.length && (
              <EmptyState searchQuery={searchQuery} onClearSearch={() => setSearchQuery('')} />
            )}

            {/* Pagination */}
            <LibraryPagination
              currentPage={currentPage}
              totalPages={coursesData?.totalPages || 0}
              onPageChange={setCurrentPage}
            />
          </>
        )}

        {/* Notification Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MemrePageContainer>
    </>
  );
};

export default PartnerLibrary;
